/**
 * App-host bootstrap voor cross-host impersonatie (ADR-0028).
 *
 * Wanneer de eindgebruiker-host geopend wordt met `?handoff=<token>` (door de
 * "open als gebruiker"-knop op de manage-host), wisselen we de token éénmalig
 * in tegen een shadow-sessie op DEZE host. Daarna strippen we de query-param
 * en herladen we de auth-status zodat de SPA als de target verschijnt.
 *
 * De hook geeft `consuming` terug zodat de app-root een loader kan tonen i.p.v.
 * even het login-scherm te flashen terwijl de consume loopt.
 */
import { useEffect, useState } from 'react';

import { api, ensureCsrf } from '@shared/api/client';
import { useAuthStore } from '@shared/stores/auth';

function handoffTokenFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get('handoff');
}

export function useHandoffConsume(enabled: boolean): { consuming: boolean } {
  const [consuming, setConsuming] = useState<boolean>(
    () => enabled && handoffTokenFromUrl() !== null,
  );

  useEffect(() => {
    if (!enabled) return;
    const token = handoffTokenFromUrl();
    if (token === null) return;

    let cancelled = false;

    void (async () => {
      try {
        // Stateful POST → CSRF-cookie eerst.
        await ensureCsrf();
        await api.post('/api/v1/impersonation/consume', { token });
      } catch {
        // Ongeldige/verlopen token → de gebruiker landt op het normale
        // login-scherm; geen harde fout tonen.
      } finally {
        // Strip de handoff-param uit de URL (geen token in history/bookmarks).
        const params = new URLSearchParams(window.location.search);
        params.delete('handoff');
        const qs = params.toString();
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash,
        );

        // Auth-status opnieuw ophalen zodat de store de target reflecteert.
        try {
          await useAuthStore.getState().fetchMe();
        } catch {
          // genegeerd — fetchMe zet zelf de juiste (uitgelogde) staat
        }

        if (!cancelled) setConsuming(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { consuming };
}
